// =============================================================================
// NOTIFICATION PANEL COMPONENT
// =============================================================================

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from '../../stores/notificationStore.js';
import { NotificationItem } from './NotificationItem.js';

// =============================================================================
// COMPONENT
// =============================================================================

export function NotificationPanel() {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    isLoading,
    hasMore,
    isPanelOpen,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    closePanel,
  } = useNotificationStore();

  // Load notifications when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      void loadNotifications();
    }
  }, [isPanelOpen, loadNotifications]);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Check if click was on the bell button
        const bellButton = document.querySelector('[data-notification-bell]');
        if (bellButton && bellButton.contains(event.target as Node)) {
          return;
        }
        closePanel();
      }
    }

    if (isPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }
  }, [isPanelOpen, closePanel]);

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoading) {
      void loadMoreNotifications();
    }
  };

  if (!isPanelOpen) return null;

  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-surface-2 border border-line-2 rounded-lg shadow-xl z-50 overflow-hidden"
      data-testid="notification-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-line-2">
        <h3 className="text-sm font-semibold text-ink-1">{t('notifications.title')}</h3>
        {hasUnread && (
          <button
            className="text-xs text-primary hover:text-primary-hover transition-colors"
            onClick={markAllAsRead}
          >
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* Notification list */}
      <div
        className="max-h-96 overflow-y-auto divide-y divide-line-1"
        onScroll={handleScroll}
      >
        {notifications.length === 0 && !isLoading ? (
          <div className="p-8 text-center text-ink-3">
            <div className="text-4xl mb-2">🔔</div>
            <p className="text-sm">{t('notifications.empty')}</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={markAsRead}
              onDelete={deleteNotification}
            />
          ))
        )}

        {isLoading && (
          <div className="p-4 text-center">
            <div className="w-6 h-6 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2 border-t border-line-2 text-center">
        <a
          href="/settings"
          className="text-xs text-ink-3 hover:text-primary-hover transition-colors"
          onClick={closePanel}
        >
          {t('notifications.settings')}
        </a>
      </div>
    </div>
  );
}
